import {
    ContractEntry,
    embedErrorCodes,
    EmbedEventErrorDetail,
    eventErrorType,
} from '@thoughtspot/embed-util';
import { create } from '@thoughtspot/logger';
import { EventType } from './constants';
import { dispatchNonMemoEvent } from './handlers';
import { getHostEventConfig } from './utils';

const logger = create('he-validation');

type PrimitiveType =
    | 'string'
    | 'number'
    | 'boolean'
    | 'any'
    | 'void'
    | 'array'
    | 'object';

const primitiveValidators: Record<PrimitiveType, (v: any) => boolean> = {
    string: v => typeof v === 'string',
    number: v => typeof v === 'number' && !Number.isNaN(v),
    boolean: v => typeof v === 'boolean',
    any: () => true,
    void: () => true,
    array: v => Array.isArray(v),
    object: v => typeof v === 'object' && v !== null && !Array.isArray(v),
};

const formatPath = (path?: string) => path || 'root';

const isValidSchema = (schema: any): boolean => {
    return schema && typeof schema === 'object';
};

/**
 * Generates a human-readable description of expected schema
 */
const describeSchema = (schema: ContractEntry): string => {
    if (!schema) return 'any value';

    if (schema.enum?.length) {
        return `one of [${schema.enum.join(', ')}]`;
    }

    if (schema.unionOf?.length) {
        return `${schema.unionOf.join(' or ')}`;
    }

    if (schema.oneOf?.length) {
        return 'one of multiple formats';
    }

    if (schema.type === 'array') {
        const itemType =
            typeof schema.arrayOf === 'string'
                ? schema.arrayOf
                : schema.arrayOf?.type || 'any';
        return `array of ${itemType}`;
    }

    if (schema.type === 'object' && schema.properties) {
        const props = Object.entries(schema.properties)
            .map(([key, propSchema]) => {
                const isOptional = (propSchema as any).optional;
                return isOptional ? `${key}?` : key;
            })
            .join(', ');
        return `object {${props}}`;
    }

    if (schema.type) {
        return schema.type;
    }

    return 'valid value';
};

const validateEnum = (
    schema: ContractEntry,
    payload: any,
    path: string,
    errors: string[],
): boolean => {
    if (!schema.enum?.length) return true;

    const isValid = schema.enum.includes(payload);

    if (!isValid) {
        errors.push(
            `Expected one of [${schema.enum.join(', ')}] at ${formatPath(
                path,
            )}, got "${payload}"`,
        );
    }

    return isValid;
};

const validateContract = (
    schema: ContractEntry,
    payload: any,
    path: string,
    errors: string[],
): boolean => {
    if (!isValidSchema(schema)) return true;

    if (payload == null) {
        if (!schema.optional) {
            const expected = describeSchema(schema);
            errors.push(
                `Missing required value at ${formatPath(
                    path,
                )} (expected: ${expected})`,
            );
            return false;
        }
        return true;
    }

    if (schema.oneOf?.length) {
        const isValid = schema.oneOf.some((s: ContractEntry) =>
            validateContract(s, payload, path, []),
        );

        if (!isValid) {
            errors.push(
                `Value at ${formatPath(path)} does not match expected format`,
            );
        }

        return isValid;
    }

    if (schema.unionOf?.length) {
        const isValid = schema.unionOf.some((primitiveType: PrimitiveType) => {
            return (
                primitiveType in primitiveValidators &&
                primitiveValidators[primitiveType](payload)
            );
        });

        if (!isValid) {
            errors.push(
                `Expected one of [${schema.unionOf.join(', ')}] at ${formatPath(
                    path,
                )}, got ${typeof payload}`,
            );
        }

        return isValid;
    }

    if (schema.type === 'array') {
        if (!Array.isArray(payload)) {
            errors.push(
                `Expected array at ${formatPath(path)}, got ${typeof payload}`,
            );
            return false;
        }

        if (!schema.arrayOf) return true;

        const itemSchema: any =
            typeof schema.arrayOf === 'string'
                ? { type: schema.arrayOf as PrimitiveType }
                : schema.arrayOf;

        return payload.every((item, i) =>
            validateContract(itemSchema, item, `${path}[${i}]`, errors),
        );
    }

    if (schema.type === 'object') {
        if (typeof payload !== 'object' || Array.isArray(payload)) {
            errors.push(
                `Expected object at ${formatPath(path)}, got ${typeof payload}`,
            );
            return false;
        }

        if (!schema.properties) return true;

        // Check for unknown properties in payload that aren't in schema
        const schemaKeys = new Set(Object.keys(schema.properties));
        const payloadKeys = Object.keys(payload);
        const unknownKeys = payloadKeys.filter(key => !schemaKeys.has(key));

        if (unknownKeys.length > 0) {
            const location = path ? ` at ${formatPath(path)}` : '';
            const allowedProps = Array.from(schemaKeys).join(', ');
            errors.push(
                `Unknown property: "${unknownKeys.join(', ')}"${location}. ` +
                    `Allowed properties: [${allowedProps}]`,
            );
            return false;
        }

        const entries = Object.entries(schema.properties);
        return entries.every(([key, propSchema]) => {
            const nextPath = path ? `${path}.${key}` : key;
            const exists = key in payload;

            if (!(propSchema as any).optional && !exists) {
                const expected = describeSchema(propSchema);
                const location = path ? ` at ${formatPath(path)}` : '';
                const errorMsg = `Missing required property: ${key}${location} (expected: ${expected})`;
                errors.push(errorMsg);
                return false;
            }

            if (
                exists &&
                !validateContract(propSchema, payload[key], nextPath, errors)
            ) {
                return false;
            }

            return true;
        });
    }

    if (schema.type && schema.type in primitiveValidators) {
        const isValid = primitiveValidators[schema.type as PrimitiveType](
            payload,
        );

        if (!isValid) {
            errors.push(
                `Expected ${schema.type} at ${formatPath(
                    path,
                )}, got ${typeof payload}`,
            );
            return false;
        }

        return validateEnum(schema, payload, path, errors);
    }

    return true;
};

// Returns the dispatched error detail so a refusing caller can also answer the
// host event's reply port; returns undefined when the error is only logged.
export const dispatchValidationError = (
    errorMessage: string,
): EmbedEventErrorDetail | undefined => {
    const hostEventConfig = getHostEventConfig();
    const isByPassHostEventsPayloadValidationDisabled =
        !hostEventConfig.useHostEventsV2 ||
        hostEventConfig.shouldBypassPayloadValidation;
    if (isByPassHostEventsPayloadValidationDisabled) {
        logger.warn(`Host Event Validation failed: ${errorMessage}`);
        return undefined;
    }
    const errorDetail: EmbedEventErrorDetail = {
        errorType: eventErrorType.ValidationError,
        message: errorMessage,
        code: embedErrorCodes.HOST_EVENT_VALIDATION,
    };
    dispatchNonMemoEvent(EventType.Error, errorDetail);
    return errorDetail;
};

/**
 * Validates payload against contract schema and dispatches errors if invalid.
 * Returns true if no schema or valid, false if schema exists and validation fails.
 */
export const validateRequiredParams = (
    schema: ContractEntry,
    payload: any,
    path = '',
    context?: any,
): { isValid: boolean; errorMessage: string } => {
    if (!isValidSchema(schema)) return { isValid: true, errorMessage: '' };

    const errors: string[] = [];
    const isValid = validateContract(schema, payload, path, errors);
    const errorMessage = `In context ${context}: ${errors.join(', ')}`;
    if (!isValid && errors.length > 0) {
        dispatchValidationError(errorMessage);
    }

    return { isValid, errorMessage };
};
